import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2, Mic, Square, Volume2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const tidy = (s) => String(s || "").toLowerCase().replace(/[^\p{L}\p{N}\s]/gu, "").trim();

const closeness = (said, heard) => {
  const want = tidy(said).split(/\s+/).filter(Boolean);
  const got = new Set(tidy(heard).split(/\s+/).filter(Boolean));
  if (want.length === 0) return 0;
  return Math.round((want.filter((w) => got.has(w)).length / want.length) * 100);
};

export const Revise = ({ rows, onClose, onPractised }) => {
  const [at, setAt] = useState(0);
  const [showNote, setShowNote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [heard, setHeard] = useState(null);
  const recorder = useRef(null);
  const chunks = useRef([]);

  const row = rows[at];

  useEffect(() => {
    setShowNote(false);
    setHeard(null);
  }, [at]);

  useEffect(() => {
    const keys = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setAt((i) => Math.min(i + 1, rows.length - 1));
      if (e.key === "ArrowLeft") setAt((i) => Math.max(i - 1, 0));
    };
    window.addEventListener("keydown", keys);
    return () => window.removeEventListener("keydown", keys);
  }, [rows.length, onClose]);

  const marked = useRef(false);

  const notePractice = async () => {
    if (marked.current) return;
    marked.current = true;
    try {
      const { data } = await api.post("/revise/done", { count: 1 });
      onPractised?.(data);
    } catch {
      marked.current = false;
    }
  };

  const listen = async () => {
    notePractice();
    setBusy(true);
    try {
      const res = await api.get(`/rooms/${row.room_id}/messages/${row.message_id}/speech`,
                                { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch {
      toast.error("Could not read that one out");
    } finally {
      setBusy(false);
    }
  };

  const startSaying = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      notePractice();
      const rec = new MediaRecorder(stream);
      chunks.current = [];
      rec.ondataavailable = (e) => chunks.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks.current, { type: "audio/webm" });
        const form = new FormData();
        form.append("audio", blob, "revise.webm");
        setBusy(true);
        try {
          const { data } = await api.post("/chat/transcribe", form);
          setHeard(data.text || "");
        } catch {
          toast.error("Could not hear that clearly");
        } finally {
          setBusy(false);
        }
      };
      recorder.current = rec;
      rec.start();
      setRecording(true);
    } catch {
      toast.error("Microphone permission denied");
    }
  };

  const stopSaying = () => {
    recorder.current?.stop();
    recorder.current = null;
    setRecording(false);
  };

  if (!row) return null;
  const score = heard === null ? null : closeness(row.text, heard);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-page/95 backdrop-blur-sm"
         data-testid="revise-mode">
      <div className="flex items-center justify-between px-5 py-4">
        <p className="text-[11px] uppercase tracking-widest text-ink-soft" data-testid="revise-count">
          Revising · {at + 1} of {rows.length}
        </p>
        <button data-testid="revise-close" onClick={onClose}
                className="rounded-full p-2 text-ink-soft transition-colors hover:text-ink">
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="flex flex-1 flex-col items-center justify-center px-6 pb-16 text-center">
        <p className="text-[11px] uppercase tracking-widest text-ink-soft">
          {row.folder || row.room_name}
        </p>
        <p data-testid="revise-phrase" dir="auto"
           className="mt-4 max-w-3xl font-display text-3xl font-bold leading-snug tracking-tight sm:text-4xl lg:text-5xl">
          {row.text}
        </p>

        {showNote && row.note && (
          <p className="mt-5 max-w-xl text-base text-brand" data-testid="revise-note">{row.note}</p>
        )}

        {heard !== null && (
          <div className="mt-6 max-w-xl" data-testid="revise-heard">
            <p className="text-[11px] uppercase tracking-widest text-ink-soft">You said</p>
            <p className="mt-1 text-base" dir="auto">{heard || "— nothing came through"}</p>
            <p className={`mt-2 text-sm font-semibold ${score >= 70 ? "text-emerald-300" : "text-amber-300"}`}
               data-testid="revise-score">
              {score >= 70 ? `Close enough · ${score}%` : `Have another go · ${score}%`}
            </p>
          </div>
        )}

        <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
          <button
            data-testid="revise-listen"
            onClick={listen}
            disabled={busy}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-full bg-brand px-5 text-sm font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95 disabled:opacity-50"
          >
            {busy && !recording ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Volume2 className="h-4 w-4" />} Hear it
          </button>
          <button
            data-testid="revise-say"
            onClick={recording ? stopSaying : startSaying}
            className={`inline-flex min-h-[44px] items-center gap-2 rounded-full border px-5 text-sm font-semibold transition-colors ${
              recording ? "border-red-400 bg-red-500/15 text-red-200"
                        : "border-white/15 text-ink-soft hover:border-brand/40 hover:text-ink"}`}
          >
            {recording ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            {recording ? "Stop" : "Say it back"}
          </button>
          {row.note && (
            <button
              data-testid="revise-show-note"
              onClick={() => setShowNote((v) => !v)}
              className="text-xs text-ink-soft underline decoration-dotted hover:text-ink"
            >
              {showNote ? "Hide my note" : "Show my note"}
            </button>
          )}
        </div>

        <div className="mt-10 flex items-center gap-3">
          <button
            data-testid="revise-prev"
            onClick={() => setAt((i) => Math.max(i - 1, 0))}
            disabled={at === 0}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-ink-soft transition-colors hover:border-brand/40 hover:text-ink disabled:opacity-30"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            data-testid="revise-next"
            onClick={() => setAt((i) => Math.min(i + 1, rows.length - 1))}
            disabled={at === rows.length - 1}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-ink-soft transition-colors hover:border-brand/40 hover:text-ink disabled:opacity-30"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
